import { saveAs } from "file-saver";

export function toExcelNumber(value) {
  if (value === null || value === undefined || value === "") return "";
  const normalized = String(value).trim().replace(",", ".");
  const numberValue = Number(normalized);
  return Number.isFinite(numberValue) ? numberValue : String(value);
}

function loadExcelImage(dataUrl) {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.onload = () => resolve(image);
    image.onerror = () => reject(new Error("Fiş fotoğrafı Excel için hazırlanamadı."));
    image.src = dataUrl;
  });
}

async function prepareExcelImage(dataUrl) {
  const source = String(dataUrl || "").trim();
  const mimeMatch = source.match(/^data:image\/([^;]+);base64,/i);
  if (!mimeMatch) {
    throw new Error("Fiş fotoğrafı verisi geçersiz.");
  }

  const image = await loadExcelImage(source);
  const mimeSubtype = mimeMatch[1].toLowerCase();
  const extension = mimeSubtype === "jpg" ? "jpeg" : mimeSubtype;

  if (["jpeg", "png", "gif"].includes(extension)) {
    return {
      dataUrl: source,
      extension,
      width: image.naturalWidth || image.width,
      height: image.naturalHeight || image.height,
    };
  }

  const canvas = document.createElement("canvas");
  canvas.width = image.naturalWidth || image.width;
  canvas.height = image.naturalHeight || image.height;
  const context = canvas.getContext("2d");
  if (!context) {
    throw new Error("Fiş fotoğrafı Excel için dönüştürülemedi.");
  }

  context.drawImage(image, 0, 0, canvas.width, canvas.height);
  return {
    dataUrl: canvas.toDataURL("image/png"),
    extension: "png",
    width: canvas.width,
    height: canvas.height,
  };
}

export async function exportVehicleRowsToExcel({
  fileName,
  worksheetName,
  columns,
  rows,
  imageColumns = [],
}) {
  const { default: ExcelJS } = await import("exceljs");
  const workbook = new ExcelJS.Workbook();
  const worksheet = workbook.addWorksheet(worksheetName);

  worksheet.columns = columns;
  worksheet.addRows(rows);
  worksheet.views = [{ state: "frozen", ySplit: 1 }];
  worksheet.autoFilter = {
    from: { row: 1, column: 1 },
    to: { row: 1, column: columns.length },
  };

  const headerRow = worksheet.getRow(1);
  headerRow.height = 24;
  headerRow.font = { bold: true, color: { argb: "FFFFFFFF" } };
  headerRow.alignment = { vertical: "middle", horizontal: "center" };
  headerRow.fill = {
    type: "pattern",
    pattern: "solid",
    fgColor: { argb: "FF2563EB" },
  };

  worksheet.eachRow((row, rowNumber) => {
    if (rowNumber === 1) return;
    row.alignment = { vertical: "middle" };
    if (rowNumber % 2 === 0) {
      row.fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: "FFEFF6FF" },
      };
    }
  });

  for (const imageColumn of imageColumns) {
    const columnNumber = worksheet.getColumn(imageColumn.columnKey).number;
    const maxWidth = imageColumn.maxWidth || 112;
    const maxHeight = imageColumn.maxHeight || 72;
    const rowHeight = imageColumn.rowHeight || 60;

    for (let rowIndex = 0; rowIndex < rows.length; rowIndex += 1) {
      const dataUrl = rows[rowIndex]?.[imageColumn.dataKey];
      if (!dataUrl) continue;

      const excelImage = await prepareExcelImage(dataUrl);
      const scale = Math.min(
        maxWidth / Math.max(excelImage.width, 1),
        maxHeight / Math.max(excelImage.height, 1)
      );
      const imageId = workbook.addImage({
        base64: excelImage.dataUrl,
        extension: excelImage.extension,
      });
      const worksheetRow = worksheet.getRow(rowIndex + 2);
      worksheetRow.height = Math.max(worksheetRow.height || 0, rowHeight);
      worksheet.addImage(imageId, {
        tl: { col: columnNumber - 1 + 0.08, row: rowIndex + 1 + 0.08 },
        ext: {
          width: Math.max(1, Math.round(excelImage.width * scale)),
          height: Math.max(1, Math.round(excelImage.height * scale)),
        },
        editAs: "oneCell",
      });
    }
  }

  const buffer = await workbook.xlsx.writeBuffer();
  saveAs(
    new Blob([buffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    }),
    fileName
  );
}
